import React from 'react';

/**
 * Lightweight skeleton primitives. Renders neutral placeholder bars that match
 * the app's premium cream/green visual style. No animations heavier than a
 * subtle pulse — Tailwind's `animate-pulse` keeps GPU cost negligible.
 *
 * Used across LadderPage / ChallengesPage / PlayerPage to fill the page shell
 * immediately on navigation, so users never see a blank screen while data
 * loads.
 */

export function SkeletonBar({ width = 'w-full', height = 'h-3', className = '' }) {
  return (
    <div
      data-testid="skeleton-bar"
      className={`${width} ${height} bg-[#e3dcd1] rounded-md animate-pulse ${className}`}
    />
  );
}

/** Compact placeholder for a player row in the ladder list. */
export function LadderRowSkeleton() {
  return (
    <div
      data-testid="ladder-row-skeleton"
      className="flex items-center gap-3 bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] p-3"
    >
      <div className="w-10 h-10 rounded-full bg-[#e3dcd1] animate-pulse" />
      <div className="flex-1 space-y-2">
        <SkeletonBar width="w-2/3" />
        <SkeletonBar width="w-1/3" height="h-2.5" />
      </div>
      <div className="w-20 h-8 bg-[#e3dcd1] rounded-full animate-pulse" />
    </div>
  );
}

/** Placeholder for a challenge/match card. */
export function ChallengeCardSkeleton() {
  return (
    <div
      data-testid="challenge-card-skeleton"
      className="bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] p-4 space-y-3"
    >
      <div className="flex justify-between items-start">
        <div className="space-y-2 flex-1">
          <SkeletonBar width="w-2/3" height="h-4" />
          <SkeletonBar width="w-1/4" height="h-2.5" />
        </div>
        <div className="w-16 h-5 bg-[#e3dcd1] rounded-full animate-pulse" />
      </div>
      <div className="flex gap-2">
        <div className="h-9 flex-1 bg-[#e3dcd1] rounded-full animate-pulse" />
        <div className="h-9 flex-1 bg-[#e3dcd1] rounded-full animate-pulse" />
      </div>
    </div>
  );
}

/** Repeats a skeleton component N times. */
export function SkeletonList({ count = 3, Component }) {
  return (
    <div className="space-y-2" data-testid="skeleton-list">
      {Array.from({ length: count }).map((_, i) => <Component key={i} />)}
    </div>
  );
}
